import { yupResolver } from '@hookform/resolvers/yup'
import { isBefore } from 'date-fns'
import { Controller, useForm } from 'react-hook-form'
import { Grid } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { DatePicker } from 'shared/components/DateInput/DatePicker'
import { TimePicker } from 'shared/components/DateInput/TimePicker'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { bgGray, lightGray1 } from 'shared/theme/colors'
import styled from 'styled-components'
import { mixed, object, string } from 'yup'

import { formatCurrency } from '../../helpers/formatters'
import { Repayment } from '../../models/repayments'
import { startTimeFormatter } from './PolicyRefinanceForm'

interface PolicyCreditCorrectionFormProps {
  policy: Repayment
  onSubmit: (data: CorrectionFormValues) => void
  onCancel: () => void
  isLoading: boolean
  confirmReady: boolean
}

export interface CorrectionFormValues {
  amount: string
  weeklyDeduction: string
  startDate: Date | null
  startTime: Date | null
}

const CorrectionFormDefaultValues: CorrectionFormValues = {
  amount: '',
  weeklyDeduction: '',
  startDate: null,
  startTime: null,
}

const CorrectionFormSchema = () =>
  object().shape({
    amount: string().test(function (value) {
      if (!value) {
        return this.createError({ message: 'Amount is required' })
      }

      if (isNaN(parseFloat(value))) {
        return this.createError({ message: 'Amount must be a number' })
      }

      if (parseFloat(value) <= 0) {
        return this.createError({ message: 'Amount must be greater than 0' })
      }

      return true
    }),
    weeklyDeduction: string().test(function (value) {
      if (!value) {
        return this.createError({ message: 'Weekly deduction is required' })
      }

      if (isNaN(parseFloat(value))) {
        return this.createError({ message: 'Weekly deduction must be a number' })
      }

      if (parseFloat(value) <= 0) {
        return this.createError({ message: 'Weekly deduction must be greater than 0' })
      }

      return true
    }),

    startDate: mixed().test(function (value) {
      if (!value) {
        return this.createError({ message: 'Start date is required' })
      }

      const date = startTimeFormatter(value, this.parent.startTime)

      if (isBefore(new Date(date), new Date()))
        return this.createError({ message: 'Start date cannot be in the past' })

      return true
    }),

    startTime: mixed().test(function (value) {
      if (!value) {
        return this.createError({ message: 'Start time is required' })
      }

      const date = startTimeFormatter(this.parent.startDate, value)

      if (isBefore(new Date(date), new Date()))
        return this.createError({ message: 'Start date cannot be in the past' })

      return true
    }),
  })

export function CorrectionAdvanceForm({
  policy,
  onSubmit,
  onCancel,
  isLoading,
  confirmReady,
}: PolicyCreditCorrectionFormProps) {
  const { control, watch, handleSubmit } = useForm<CorrectionFormValues>({
    mode: 'onSubmit',
    defaultValues: CorrectionFormDefaultValues,
    resolver: yupResolver(CorrectionFormSchema()),
  })

  const amount = watch('amount')
  const weeklyDeduction = watch('weeklyDeduction')

  return (
    <FlexBox isColumn gap="4px" isFullWidth alignItems="stretch">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid columns="2fr 1fr" gap="16px">
          <Text variant="header3" align="left" fontColor={lightGray1} mb={3}>
            Current total deduction amount
          </Text>

          <Text variant="header3" align="right">
            {policy.repayment_details.remaining_payment === 'N/A'
              ? 'N/A'
              : formatCurrency(policy.repayment_details.remaining_payment)}
          </Text>
        </Grid>

        <Grid columns="minmax(0, 1fr) minmax(0, 180px)" xsColumns="minmax(0, 1fr) minmax(0, 140px)" mb={3}>
          <Controller
            render={({ field, fieldState }) => (
              <StyledDatePicker {...field} label="Start date" error={fieldState.error?.message} disablePast />
            )}
            name="startDate"
            control={control}
          />

          <Controller
            render={({ field, fieldState }) => (
              <StyledTimePicker {...field} label="Start time (24 Hour)" error={fieldState.error?.message} />
            )}
            name="startTime"
            control={control}
          />
        </Grid>

        <Controller
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label="New current balance"
              backgroundColor={bgGray}
              error={fieldState.error?.message}
              isCurrency
            />
          )}
          control={control}
          name="amount"
        />

        <Controller
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label="New weekly deduction"
              backgroundColor={bgGray}
              error={fieldState.error?.message}
              isCurrency
            />
          )}
          control={control}
          name="weeklyDeduction"
        />

        <Grid columns="1fr 1fr" gap="16px">
          <Text variant="header3" align="left" fontColor={lightGray1} mb={3}>
            Billing periods
          </Text>

          <Text variant="header3" align="right">
            {amount && weeklyDeduction && !isNaN(parseFloat(weeklyDeduction)) && !isNaN(parseFloat(amount))
              ? Math.ceil(parseFloat(amount) / parseFloat(weeklyDeduction))
              : '-'}
          </Text>
        </Grid>

        <Box mb={6} />

        <FlexBox isColumn isFullWidth justifyContent="center" alignItems="center" mt={9}>
          <Button type="submit" variant="primary" isLoading={isLoading} isDisabled={!confirmReady} mb={1}>
            Confirm
          </Button>
          <Button variant="flat" onClick={onCancel}>
            Cancel
          </Button>
        </FlexBox>
      </form>
    </FlexBox>
  )
}

export function CorrectionSubscriptionForm({
  policy,
  onSubmit,
  onCancel,
  isLoading,
  confirmReady,
}: PolicyCreditCorrectionFormProps) {
  const { control, handleSubmit } = useForm<CorrectionFormValues>({
    mode: 'onSubmit',
    defaultValues: CorrectionFormDefaultValues,
    resolver: yupResolver(CorrectionFormSchema()),
  })

  return (
    <FlexBox isColumn gap="4px" isFullWidth alignItems="stretch">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid columns="1fr 1fr" gap="16px">
          <Text variant="header3" align="left" fontColor={lightGray1} mb={3}>
            Current balance
          </Text>

          <Text variant="header3" align="right">
            {policy
              ? policy.repayment_details.underpayment_from_previous_periods_as_of_today === 'N/A'
                ? 'N/A'
                : formatCurrency(policy.repayment_details.underpayment_from_previous_periods_as_of_today)
              : ''}
          </Text>
        </Grid>

        <Grid columns="1fr 1fr" gap="16px">
          <Text variant="header3" align="left" fontColor={lightGray1} mb={3}>
            Weekly deduction
          </Text>

          <Text variant="header3" align="right">
            {policy
              ? policy.repayment_details.amount_owed_per_period === 'N/A'
                ? 'N/A'
                : formatCurrency(policy.repayment_details.amount_owed_per_period)
              : ''}
          </Text>
        </Grid>

        <Grid columns="minmax(0, 1fr) minmax(0, 180px)" xsColumns="minmax(0, 1fr) minmax(0, 140px)" mb={3}>
          <Controller
            render={({ field, fieldState }) => (
              <StyledDatePicker {...field} label="Start date" error={fieldState.error?.message} disablePast />
            )}
            name="startDate"
            control={control}
          />

          <Controller
            render={({ field, fieldState }) => (
              <StyledTimePicker {...field} label="Start time (24 Hour)" error={fieldState.error?.message} />
            )}
            name="startTime"
            control={control}
          />
        </Grid>

        <Controller
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label="New current balance"
              backgroundColor={bgGray}
              error={fieldState.error?.message}
              isCurrency
            />
          )}
          control={control}
          name="amount"
        />

        <Controller
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label="New weekly deduction"
              backgroundColor={bgGray}
              error={fieldState.error?.message}
              isCurrency
            />
          )}
          control={control}
          name="weeklyDeduction"
        />

        <FlexBox isColumn isFullWidth justifyContent="center" alignItems="center" mt={9}>
          <Button type="submit" variant="primary" isLoading={isLoading} isDisabled={!confirmReady} mb={1}>
            Confirm
          </Button>
          <Button variant="flat" onClick={onCancel}>
            Cancel
          </Button>
        </FlexBox>
      </form>
    </FlexBox>
  )
}

const StyledTimePicker = styled(TimePicker)`
  background-color: ${bgGray};
  width: 100% !important;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: none;
`

const StyledDatePicker = styled(DatePicker)`
  background-color: ${bgGray};
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`
